import { Button, showToast, Tables } from '@deeptrue-frontend/ui';
import api from 'api';
import React, { useEffect, useState } from 'react';
import { useFormatMessage } from 'components/IntlProvider';
import styled from 'styled-components/macro';
import { Grid } from '@material-ui/core';
import ModalWithTitle from 'components/Modal/ModalWIthTitle';
import {
  GenerateLinkForm,
  InitialValues,
} from '../GenererateLinkForm/GenerateLinkForm';
import { Link, LinkType } from './Link';

const { Table, TableBody, TableHeader } = Tables;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BottomSection = styled.div`
  margin-top: 3rem;
  display: flex;
  justify-content: space-between;
`;

const Wrapper = styled.div`
  width: 70vw;
  max-width: 800px;
`;

interface Props {
  organization: any;
  surveyId: string;
  closeModal: () => void;
}

export const LinkList = ({ surveyId, organization, closeModal }: Props) => {
  const [links, setLinks] = useState<LinkType[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialValues, setInitialValues] = useState<
    InitialValues | undefined
  >();
  const t = useFormatMessage();

  useEffect(() => {
    if (surveyId) {
      api.resources
        .getSharingLinks({
          organizationCodename: organization,
          surveyId: surveyId,
        })
        .then(({ data }) => {
          setLinks(data.results);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
          showToast({
            type: 'error',
            title: t(
              'errors.toast.general',
              'Something went wrong, please contact administrator'
            ),
          });
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  const removeElement = (index: number) => {
    const newLinks = [...links];
    newLinks.splice(index, 1);

    setLinks(newLinks);
  };

  const areLinks = links.length > 0;

  const addLink = (link: LinkType) => {
    setLinks([...links, link]);
  };

  const updateLink = (linkDetails: LinkType) => {
    const newLinks = [...links];
    const editedLinkPosition = newLinks.findIndex(
      (link) => link.id === linkDetails.id
    );

    newLinks.splice(editedLinkPosition, 1, linkDetails);

    setLinks(newLinks);
  };

  return (
    <Wrapper>
      <Header>
        <Grid container>
          <Table>
            <TableHeader
              cells={[
                {
                  text: t('project.share.shareReportLinks.title"', 'Title'),
                  props: {
                    maxWidth: '300px',
                    width: '300px',
                    padding: '1rem 0.5rem',
                    height: '3rem !important',
                  },
                },
                {
                  text: t('organization.columns.status', 'Status'),
                  props: {
                    padding: '1rem 0.5rem',
                    height: '3rem !important',
                  },
                },
                {
                  text: t('organization.columns.resources', 'Resources'),
                  props: {
                    padding: '1rem 0.5rem',
                    height: '3rem !important',
                    width: '150px',
                  },
                },
              ]}
            />
            <TableBody
              isLoading={isLoading}
              withLoader
              isData={areLinks}
              noDataText={t(
                'noresults',
                'No results. We have nothing to show here.'
              )}
            >
              {links.map((sharedLink: LinkType, index: number) => {
                return (
                  <Link
                    key={sharedLink.id}
                    linkDetails={sharedLink}
                    surveyId={surveyId}
                    organization={organization}
                    index={index}
                    removeElement={removeElement}
                    setInitialValues={setInitialValues}
                    setIsFormOpen={setIsFormOpen}
                    updateLink={updateLink}
                  />
                );
              })}
            </TableBody>
          </Table>
        </Grid>
      </Header>

      <BottomSection style={{ marginTop: areLinks ? '2rem' : '5rem' }}>
        <Button onClick={() => setIsFormOpen(true)} size="small">
          {t('project.share.shareReportLinks.createLinks', 'Create links')}
        </Button>
        <Button onClick={closeModal} size="small" variant="outlined">
          {t('project.share.shareReportLinks.close', 'Close')}
        </Button>
      </BottomSection>

      <ModalWithTitle
        title={t('project.share.generateLink', 'Generate link')}
        open={isFormOpen}
        onClose={() => {
          setInitialValues(undefined);
          setIsFormOpen(false);
        }}
        disablePadding
      >
        <GenerateLinkForm
          setIsFormOpen={setIsFormOpen}
          surveyId={surveyId}
          organization={organization}
          initialValues={initialValues}
          setInitialValues={setInitialValues}
          addLink={addLink}
          updateLink={updateLink}
        />
      </ModalWithTitle>
    </Wrapper>
  );
};
